import { DetailShell } from "@/components/DetailShell"
import { Field } from "@/components/Field"
import { Section } from "@/components/Section"
import { client } from "@/lib/client"
import { dash } from "@/lib/utils"
import { useApi, type Nav } from "@/lib/hooks"

const DOC_TYPE_LABELS: Record<string, string> = {
  regulation: "Regulation",
  statute: "Statute",
  statute_compilation: "Statute (Compiled)",
  session_law: "Session Law",
  constitution: "Constitution",
  court_rule: "Court Rule",
}

const DOC_TYPE_PATHS: Record<string, string> = {
  regulation: "/regulations",
  statute: "/statutes",
  statute_compilation: "/statutes/compiled",
  session_law: "/statutes/session-laws",
  constitution: "/constitution",
  court_rule: "/court-rules",
}

export function LawDetail({ id, nav }: { id: string; nav: Nav }) {
  const { data, err } = useApi(() => client.api.law[":id"].$get({ param: { id } }), [id])
  return (
    <DetailShell
      title={dash(data?.citation)}
      backTo={DOC_TYPE_PATHS[data?.documentType ?? ""] ?? "/"}
      nav={nav}
      data={data}
      err={err}
    >
      {data && (
        <div className="space-y-6">
          <div className="space-y-1">
            <Field label="Type" value={DOC_TYPE_LABELS[data.documentType ?? ""] ?? dash(data.documentType)} />
            <Field label="Jurisdiction" value={dash(data.state)} />
            <Field label="Path" value={dash(data.displayPath)} />
            <Field label="Title" value={dash(data.titleName)} />
            {data.chapterName && <Field label="Chapter" value={data.chapterName} />}
            <Field label="Section" value={dash(data.sectionTitle)} />
            <Field label="Status" value={dash(data.actStatus)} />
            <Field label="Year" value={dash(data.year)} />
            {data.lastAmendedYear != null && <Field label="Last amended" value={data.lastAmendedYear} />}
            <Field label="Words" value={dash(data.wordCount)} />
            <Field
              label="Source"
              value={
                data.sourceUrl ? (
                  <a className="text-primary hover:underline" href={data.sourceUrl} target="_blank" rel="noreferrer">
                    {data.sourceUrl}
                  </a>
                ) : (
                  "—"
                )
              }
            />
            {data.currencyNote && <Field label="Currency note" value={data.currencyNote} />}
            {data.adoptingCitations && data.adoptingCitations.length > 0 && (
              <Field label="Adopting citations" value={data.adoptingCitations.join("; ")} />
            )}
          </div>

          <Section title="Text">
            {data.text ? (
              <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap rounded border p-3 text-sm">
                {data.text}
              </pre>
            ) : (
              <p className="text-sm text-muted-foreground">No document body.</p>
            )}
          </Section>
        </div>
      )}
    </DetailShell>
  )
}
